import {
  Button,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import "./style.css";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useFormik } from "formik";
import AxiosInstance from "../../utils/axiosInstance";
import { useLocation, useNavigate } from "react-router-dom";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import * as Yup from "yup";
import showToast from "../Toast/Toster";
import { WhiteLoaderComponent, OrangeButton } from "../Icon/Index";
import PasswordValidation from "../Password/PasswordValidation";
import InputText from "../InputFields/InputText";
import { useUserType } from "../../contexts/UserTypeContext";
import BlueButton from "../Button/BlueButton";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showCPassword, setShowCPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);
  const { userType, logo } = useUserType();
  const [randomPassName] = useState(
    `pass_${Math.random().toString(36).slice(2)}`
  );
  const [randomConfirmPassName] = useState(
    `pass_${Math.random().toString(36).slice(2)}`
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlToken = searchParams.get("token");

    if (!urlToken) {
      setTokenExpired(true);
      setError("No token provided. Please request a new password reset link.");
      return;
    }

    setToken(urlToken);
    setIsLoading(true);

    AxiosInstance.get(`/v1/forget-pass/check_forget_token_status/${urlToken}`)
      .then((response) => {
        const data = response?.data;
        setIsLoading(false);
        if (data?.expired) {
          setTokenExpired(true);
          setError(
            "The password reset link has expired. Please request a new one."
          );
          showToast.error(
            "The password reset link has expired. Please request a new one.",
            {
              position: "top-center",
            }
          );
        } else {
          setEmail(data?.email || "");
          if (data?.roles && data.roles.length > 0) {
            setRoles(data.roles);
          }
        }
      })
      .catch((error) => {
        console.error("Error checking token status:", error);
        setIsLoading(false);
        setTokenExpired(true);
        setError("Invalid token. Please request a new password reset link.");
        showToast.error(
          "Invalid token. Please request a new password reset link.",
          {
            position: "top-center",
            autoClose: 3000,
          }
        );
        setTimeout(() => {
          navigate("/auth/forgot-password");
        }, 100);
      });
  }, [location.search, navigate]);

  const formik = useFormik({
    initialValues: {
      [randomPassName]: "",
      [randomConfirmPassName]: "",
    },
    validationSchema: Yup.object({
      [randomPassName]: PasswordValidation,
      [randomConfirmPassName]: Yup.string()
        .oneOf([Yup.ref(randomPassName), null], "Passwords must match")
        .required("Confirm password is required"),
    }),
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        setError("");

        const response = await AxiosInstance.put(
          `/v1/forget-pass/reset_forget_passwords/${token}`,
          {
            Password: values[randomPassName],
            role: selectedRole || roles[0],
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response?.status === 200) {
          showToast.success("Password updated successfully!", {
            position: "top-center",
            autoClose: 2000,
          });
          navigate("/auth/login");
        }
      } catch (error) {
        console.error("Password reset error:", error);
        setIsLoading(false);
        let errorMessage =
          "New password can not be the same as the old password. Please try again.";
        let redirect = false;

        if (error?.response?.status === 401) {
          if (error?.response?.data?.error === "TOKEN_EXPIRED") {
            errorMessage =
              "The password reset link has expired. Please request a new one.";
            redirect = true;
          } else if (error?.response?.data?.error === "TOKEN_ALREADY_USED") {
            errorMessage =
              "This password reset link has already been used. Please request a new one.";
            redirect = true;
          } else if (error?.response?.data?.error === "INVALID_TOKEN") {
            errorMessage = "The password reset link is invalid.";
            redirect = true;
          }
        } else if (error?.response?.status === 400) {
          if (error?.response?.data?.error === "SAME_PASSWORD") {
            errorMessage =
              "New password cannot be the same as the old password.";
          } else if (error?.response?.data?.error === "NEW_PASSWORD_REQUIRED") {
            errorMessage = "New password is required.";
          }
        }

        setError(errorMessage);
        showToast.warning(errorMessage, {
          position: "top-center",
          autoClose: 3000,
        });

        if (redirect) {
          navigate("/auth/forgot-password");
        }
      }
    },
  });

  if (isLoading) {
    return (
      <Grid style={{ padding: "20px", textAlign: "center" }}>
        <WhiteLoaderComponent height="20" width="20" padding="20" />
      </Grid>
    );
  }

  // Define theme colors based on userType
  const isDonor = userType === "donor";
  const themeColors = {
    primary: isDonor ? "#68b2cb" : "var(--amber)",
    primaryLight: isDonor ? "#8ec9db" : "#f4a261",
    background: isDonor ? "linear-gradient(135deg, #E9F8FC 0%, #d4eef5 100%)" : "linear-gradient(135deg, #f8fafc 0%, #e0e7ef 100%)",
    buttonBackground: isDonor 
      ? "linear-gradient(135deg, #68b2cb 0%, #8ec9db 100%)" 
      : "linear-gradient(135deg, var(--amber) 0%, #f4a261 100%)",
    buttonHoverBackground: isDonor 
      ? "linear-gradient(135deg, #8ec9db 0%, #68b2cb 100%)" 
      : "linear-gradient(135deg, #f4a261 0%, var(--amber) 100%)",
    boxShadow: isDonor 
      ? "0 2px 8px rgba(104,178,203,0.15)" 
      : "0 2px 8px rgba(212, 148, 58,0.15)",
  };

  if (tokenExpired) {
    return (
      <Grid
        className="login"
        container
        justifyContent="center"
        alignItems="center"
        sx={{
          minHeight: "100vh",
          background: themeColors.background,
        }}
      >
        <Grid
          size={{
            xs: 11,
            sm: 8,
            md: 5,
            lg: 4
          }}>
          <Paper
            elevation={4}
            sx={{
              padding: 4,
              borderRadius: 4,
              textAlign: "center",
              background: "#fff",
              boxShadow: "var(--shadow-xl)",
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 60, color: themeColors.primary, mb: 2 }} />
            <Typography
              variant="h5"
              fontWeight={700}
              color="#0f2b46"
              gutterBottom
            >
              Token Expired
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              Your password reset link has expired.
              <br />
              Please request a new reset link to continue.
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                px: 4,
                background: themeColors.buttonBackground,
                color: "#fff",
                boxShadow: themeColors.boxShadow,
                "&:hover": {
                  background: themeColors.buttonHoverBackground,
                },
              }}
              onClick={() => navigate("/auth/forgot-password")}
            >
              Request New Link
            </Button>
            <Typography
              variant="body2"
              sx={{
                mt: 3,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ChevronLeftIcon
                sx={{ fontSize: 20, color: themeColors.primary }}
              />
              <span
                onClick={() => navigate("/auth/login")}
                style={{ marginLeft: "4px" }}
              >
                Go back to{" "}
                <span style={{ color: themeColors.primary, fontWeight: 600 }}>
                  Login
                </span>
              </span>
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid className="login">
      <Grid container spacing={0} className="vh-100" style={{ margin: 0, padding: 0 }}>
        <Grid
          className="d-flex justify-content-center  align-items-center pt-3 forgatePasswordNoneHe forgotTopGive"
          size={{
            xs: 12,
            md: 6
          }}>
          <Grid style={{width: "100%",display: "flex",flexDirection: "column",alignItems: "center"}}>
          <Typography
            className="app-logo forgotpassWordImageSet"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {/* JOB-3155: inherit `.app-logo` sizing (~65px) from Login screen instead of the oversized 500px inline width. */}
            <img src={logo} alt="logo" style={{ marginBottom: "50px" }} />
          </Typography>
          <Grid
            style={{
              width: "80%",
            }}
            className="d-flex flex-direction-column align-items-center p-2 maon-forget-password"
          >
            <form
              onSubmit={formik?.handleSubmit}
              className="d-flex flex-column justify-content-between forgoFormSubmitClass"
              style={{ height: "60%",width: "100%" }}
              autoComplete="off"
              spellCheck="false"
            >
              <div style={{ display: "none" }}>
                <input type="text" id="username" name="username" />
                <input type="password" id="password" name="password" />
              </div>
              <input
                type="text"
                style={{ display: "none" }}
                readOnly={true}
                autoComplete="username"
              />

              <Typography
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  textAlign: "left",
                  marginTop: "13px",
                  display: "flex",
                  cursor: "pointer",
                  alignItems: "center",
                }}
                onClick={() => {
                  navigate("/auth/login");
                }}
              >
                <ChevronLeftIcon className="text-orange-color" style={{ fontSize: "30px" }} />
                <span className="mx-2" style={{ fontSize: "16px" }}>Go back to <span className="text-orange-color">Log In</span></span>
              </Typography>

              <Grid>
                <Typography
                  className="text forgotHeadText text-blue-color"
                  style={{ fontSize: "35px", marginTop: "20px" ,marginBottom: "50px"}}
                >
                  Reset Your Password
                </Typography>
                <Typography
                  className="text2"
                  style={{ color: "#606060", marginTop: "-50px", marginBottom: "20px" }}
                >
                  Enter a new password to reset your account.
                </Typography>

                {roles.length > 1 && (
                  <FormGroup style={{ marginTop: "20px" }}>
                    <Typography style={{ marginBottom: "10px" }}>
                      Select account type:
                    </Typography>
                    {roles.map((role) => (
                      <label
                        key={role}
                        style={{ display: "block", marginTop: "5px" }}
                      >
                        <input
                          type="radio"
                          name="role"
                          value={role}
                          checked={selectedRole === role}
                          onChange={(e) => setSelectedRole(e.target.value)}
                        />{" "}
                        {role}
                      </label>
                    ))}
                  </FormGroup>
                )}

                <FormGroup style={{ width: "100%", marginTop: "20px" }}>
                  <InputText
                    value={formik?.values[randomPassName]}
                    onChange={formik?.handleChange}
                    onBlur={formik?.handleBlur}
                    error={
                      formik?.touched[randomPassName] &&
                      Boolean(formik?.errors[randomPassName])
                    }
                    helperText={
                      formik?.touched[randomPassName] &&
                      formik?.errors[randomPassName]
                    }
                    name={randomPassName}
                    label="New Password"
                    required
                    type={showPassword ? "text" : "password"}
                    className="text-blue-color w-100 mb-3"
                    autoComplete="new-password"
                    inputProps={{
                      autoComplete: "new-password",
                      "data-lpignore": "true",
                    }}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormGroup>

                <FormGroup style={{ width: "100%" }}>
                  <InputText
                    value={formik?.values[randomConfirmPassName]}
                    onChange={formik?.handleChange}
                    onBlur={formik?.handleBlur}
                    error={
                      formik?.touched[randomConfirmPassName] &&
                      Boolean(formik?.errors[randomConfirmPassName])
                    }
                    helperText={
                      formik?.touched[randomConfirmPassName] &&
                      formik?.errors[randomConfirmPassName]
                    }
                    name={randomConfirmPassName}
                    label="Confirm New Password"
                    required
                    type={showCPassword ? "text" : "password"}
                    className="text-blue-color w-100 mb-3"
                    autoComplete="new-password"
                    inputProps={{
                      autoComplete: "new-password",
                      "data-lpignore": "true",
                    }}
                    onPaste={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowCPassword(!showCPassword)}
                          edge="end"
                        >
                          {showCPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormGroup>

                <FormGroup style={{ width: "100%" }}>
                  <OrangeButton
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      borderRadius: "4px",
                      height: "48px",
                      textTransform: "none",
                    }}
                    type="submit"
                    disabled={isLoading || (roles.length > 1 && !selectedRole)}
                    isDonationMode={userType === "donor"}
                    label={isLoading ? "Changing Password..." : "Change Password"}
                  />
                </FormGroup>
              </Grid>
            </form>
          </Grid>
          </Grid>
        </Grid>
        <Grid
          className="d-flex justify-content-center align-items-center img decreaseSizeHei"
          style={{ height: "100vh", padding: 0, margin: 0 }}
          size={{
            xs: 12,
            md: 6
          }}>
          <div
            className="firgitPasswordImg resetImg_cdn "
            style={{ height: "100vh", width: "100%" }}
          ></div>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ResetPassword;
